import React, { useState, useEffect, useCallback } from 'react';
import {
  HiOutlinePhotograph, HiHeart, HiOutlineDotsHorizontal,
  HiOutlinePencil, HiOutlineTrash,
} from 'react-icons/hi';
import { creatorAPI } from '../services/api';
import './MyPosts.css';

const postImg = (url) =>
  url ? url.replace('/upload/', '/upload/w_600,q_auto,f_auto/') : null;

const timeAgo = (ts) => {
  if (!ts) return '';
  const iso = ts.endsWith('Z') ? ts : ts + 'Z';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  if (h < 24) return `${h}h`;
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
};

/**
 * Shows the current user's own posts with inline edit + delete, so they can
 * manage their content from their profile. Reuses the same creator content
 * endpoints as the feed.
 */
const MyPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await creatorAPI.getMyContent();
      setPosts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (post) => {
    setMenuOpenId(null);
    setEditingId(post.id);
    setEditText(post.caption || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEdit = async (postId) => {
    const caption = editText.trim();
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, caption } : p)));
    setEditingId(null);
    try {
      await creatorAPI.updateContent(postId, caption);
    } catch (err) {
      load();
    }
  };

  const deletePost = async (postId) => {
    setMenuOpenId(null);
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    const prev = posts;
    setPosts((cur) => cur.filter((p) => p.id !== postId));
    try {
      await creatorAPI.deleteContent(postId);
    } catch (err) {
      setPosts(prev);
    }
  };

  if (loading) {
    return <div className="myposts-empty">Loading your posts…</div>;
  }

  if (posts.length === 0) {
    return (
      <div className="myposts-empty">
        <HiOutlinePhotograph aria-hidden="true" />
        <p>You haven't posted anything yet. Share a photo from the Feed to get noticed.</p>
      </div>
    );
  }

  return (
    <div className="myposts-grid">
      {posts.map((post) => {
        const img = postImg(post.contentUrl);
        const isEditing = editingId === post.id;
        return (
          <div key={post.id} className="myposts-card">
            <div className="myposts-card-top">
              <span className="myposts-time">{timeAgo(post.createdAt)}</span>
              <div className="myposts-menu">
                <button
                  className="myposts-menu-btn"
                  aria-label="Post options"
                  onClick={() => setMenuOpenId(menuOpenId === post.id ? null : post.id)}
                >
                  <HiOutlineDotsHorizontal />
                </button>
                {menuOpenId === post.id && (
                  <div className="myposts-menu-dropdown">
                    <button onClick={() => startEdit(post)}>
                      <HiOutlinePencil aria-hidden="true" /> Edit
                    </button>
                    <button className="myposts-menu-delete" onClick={() => deletePost(post.id)}>
                      <HiOutlineTrash aria-hidden="true" /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>

            {img && (
              <div className="myposts-image">
                <img src={img} alt={post.caption || 'Post'} loading="lazy" />
              </div>
            )}

            {isEditing ? (
              <div className="myposts-edit">
                <textarea
                  value={editText}
                  maxLength={1000}
                  autoFocus
                  onChange={(e) => setEditText(e.target.value)}
                />
                <div className="myposts-edit-actions">
                  <button className="myposts-edit-cancel" onClick={cancelEdit}>Cancel</button>
                  <button className="myposts-edit-save" onClick={() => saveEdit(post.id)}>Save</button>
                </div>
              </div>
            ) : (
              post.caption && <p className="myposts-caption">{post.caption}</p>
            )}

            <div className="myposts-stats">
              <span><HiHeart aria-hidden="true" /> {post.likesCount || 0}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MyPosts;
